/**
 * Inventory tool hooks: counts, receiving, variance.
 *
 * These call the exact same `/v1/stores/inventory/*` endpoints Cockpit's
 * `useStores.ts` calls, and now return the exact same shapes
 * (`InvCountSession`, `InvCountLine`, `InvPoLine`, `InvPoSummary`,
 * `InvItem`, `InvVarianceRow` from `@ampersandbrands/ui`) with no renaming
 * step. There used to be a UI-type layer here (`upc`, string `id`,
 * `description`) that predated the two apps sharing a backend, with adapter
 * functions mapping the real wire response onto it. That layer is gone: the
 * wire response already matches the shared types field-for-field, so the
 * adapters were pure overhead, and dropping them fixes a couple of fields
 * they had been discarding along the way (`variance_cost`, `total_qty` on a
 * PO).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  InvCountKind,
  InvCountSession,
  InvCountLine,
  InvPoLine,
  InvPoSummary,
  InvItem,
  InvVarianceRow,
  InvVarianceView,
} from '@ampersandbrands/ui';
import { apiGet, apiFetch } from './api';
import { STORES_API_BASE } from './useStores';

export const INVENTORY_API_BASE = `${STORES_API_BASE}/inventory`;

function storeQs(storeNo?: number, extra?: Record<string, string>): string {
  const params = new URLSearchParams(extra);
  if (storeNo !== undefined) params.set('store_no', String(storeNo));
  const s = params.toString();
  return s ? `?${s}` : '';
}

// ── Sessions / counting ─────────────────────────────────────────────────────

export function useInventoryCounts(storeNo?: number) {
  return useQuery<{ sessions: InvCountSession[] }, Error>({
    queryKey: ['inventory', 'sessions', storeNo ?? 'own'],
    queryFn: () => apiGet(`${INVENTORY_API_BASE}/sessions${storeQs(storeNo)}`),
    staleTime: 10_000,
    retry: false,
  });
}

export function useInventoryCountDetail(sessionId: string, storeNo?: number) {
  return useQuery<{ session: InvCountSession; lines: InvCountLine[]; po_lines: InvPoLine[] }, Error>({
    queryKey: ['inventory', 'session', sessionId, storeNo ?? 'own'],
    queryFn: () => apiGet(`${INVENTORY_API_BASE}/sessions/${sessionId}${storeQs(storeNo)}`),
    enabled: Boolean(sessionId),
    staleTime: 5_000,
    retry: false,
  });
}

export type CreateCountInput = { kind: InvCountKind; po_no?: string; note?: string; store_no?: number };

export function useCreateCountSession() {
  const qc = useQueryClient();
  return useMutation<{ session: InvCountSession }, Error, CreateCountInput>({
    mutationFn: (input) => apiPost(`${INVENTORY_API_BASE}/sessions`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', 'sessions'] });
    },
  });
}

function apiPost<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

export function useItemLookup() {
  return useMutation<
    { found: boolean; item: InvItem | null },
    Error,
    { barcode?: string; item_no?: string; store_no?: number }
  >({
    mutationFn: ({ barcode, item_no, store_no }) => {
      const params = new URLSearchParams();
      if (barcode) params.set('barcode', barcode);
      if (item_no) params.set('item_no', item_no);
      if (store_no !== undefined) params.set('store_no', String(store_no));
      return apiGet(`${INVENTORY_API_BASE}/item?${params.toString()}`);
    },
  });
}

export function useUpsertCountLine(sessionId: string) {
  const qc = useQueryClient();
  return useMutation<
    { line: InvCountLine },
    Error,
    { item_no: string; barcod?: string; qty: number; mode?: 'add' | 'replace'; store_no?: number }
  >({
    mutationFn: (input) => apiFetch(`${INVENTORY_API_BASE}/sessions/${sessionId}/lines`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', 'session', sessionId] });
    },
  });
}

export function useDeleteCountLine(sessionId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { lineId: number; store_no?: number }>({
    mutationFn: ({ lineId, store_no }) =>
      apiFetch(`${INVENTORY_API_BASE}/sessions/${sessionId}/lines/${lineId}${storeQs(store_no)}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', 'session', sessionId] });
    },
  });
}

export function useDeleteCountSession() {
  const qc = useQueryClient();
  return useMutation<void, Error, { sessionId: number; store_no?: number }>({
    mutationFn: ({ sessionId, store_no }) =>
      apiFetch(`${INVENTORY_API_BASE}/sessions/${sessionId}${storeQs(store_no)}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', 'sessions'] });
    },
  });
}

export function useSubmitCountSession(sessionId: number) {
  const qc = useQueryClient();
  return useMutation<{ session: InvCountSession }, Error, { store_no?: number }>({
    mutationFn: (input) => apiPost(`${INVENTORY_API_BASE}/sessions/${sessionId}/submit`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

// ── Purchase orders ──────────────────────────────────────────────────────────

export function useStorePos(storeNo: number | undefined, enabled = true) {
  return useQuery<{ pos: InvPoSummary[] }, Error>({
    queryKey: ['inventory', 'pos', storeNo ?? 'own'],
    queryFn: () => apiGet(`${INVENTORY_API_BASE}/pos${storeQs(storeNo)}`),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

// ── Variance ─────────────────────────────────────────────────────────────────

export type VarianceFilters = { store_no?: number; filter?: InvVarianceView };

function varianceQs(f: VarianceFilters): string {
  const params = new URLSearchParams();
  if (f.store_no !== undefined) params.set('store_no', String(f.store_no));
  params.set('filter', f.filter ?? 'variances');
  return `?${params.toString()}`;
}

export function useVariance(filters: VarianceFilters) {
  return useQuery<{ rows: InvVarianceRow[]; totals?: { lines: number; with_variance: number; needs_count: number; variance_cost: number } }, Error>({
    queryKey: ['inventory', 'variance', filters],
    queryFn: () => apiGet(`${INVENTORY_API_BASE}/variance${varianceQs(filters)}`),
    enabled: filters.store_no !== undefined,
    staleTime: 15_000,
    retry: false,
  });
}

// ── Per-item notes (audit-trail; same store_item_note + audit_log tables
// Cockpit's item notes modal uses). Sweet Spot's NotesModal shows only the
// latest note text rather than the full history, which is the modal's own
// existing single-field design; the data underneath is the real note table. ──

export type ItemNote = { id: number; note: string; created_by_email: string; created_at: string | null };

export function useItemNotes(itemNo: string | null, storeNo?: number) {
  return useQuery<{ notes: ItemNote[] }, Error>({
    queryKey: ['inventory', 'item-notes', itemNo, storeNo ?? 'own'],
    queryFn: () => apiGet(`${INVENTORY_API_BASE}/items/${itemNo}/notes${storeQs(storeNo)}`),
    enabled: Boolean(itemNo),
    staleTime: 10_000,
    retry: false,
  });
}

export function useAddItemNote() {
  const qc = useQueryClient();
  return useMutation<{ id: number }, Error, { item_no: string; note: string; store_no?: number }>({
    mutationFn: ({ item_no, note, store_no }) =>
      apiPost(`${INVENTORY_API_BASE}/items/${item_no}/notes${storeQs(store_no)}`, { note }),
    onSuccess: (_data, { item_no }) => {
      void qc.invalidateQueries({ queryKey: ['inventory', 'item-notes', item_no] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'variance'] });
    },
  });
}
